import { KPI, KPIEnrichment, KPICategory, FinancialRecordInput, FinancialRecord } from '../types/data/financialRecord';

/**
 * Predefined KPI mappings for automotive industry standard codes
 */
export const STANDARD_KPI_MAPPINGS: Record<string, KPIEnrichment> = {
  'A0100.1': {
    code: 'A0100.1',
    displayName: 'New Vehicle Units Sold',
    description: 'Total number of new vehicles sold in the period',
    category: KPICategory.SALES,
    subcategory: 'New Vehicles',
    unit: 'units',
    dataType: 'units'
  },
  'A0100.2': {
    code: 'A0100.2',
    displayName: 'New Vehicle Sales Revenue',
    description: 'Total revenue from new vehicle sales',
    category: KPICategory.SALES,
    subcategory: 'New Vehicles',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0100.3': {
    code: 'A0100.3',
    displayName: 'New Vehicle Average Selling Price',
    description: 'Average selling price per new vehicle',
    category: KPICategory.SALES,
    subcategory: 'New Vehicles',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0100.4': {
    code: 'A0100.4',
    displayName: 'New Vehicle Gross Profit',
    description: 'Total gross profit from new vehicle sales',
    category: KPICategory.FINANCE,
    subcategory: 'New Vehicles',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0480.1': {
    code: 'A0480.1',
    displayName: 'Finance & Insurance Income',
    description: 'Income from finance and insurance products',
    category: KPICategory.FINANCE,
    subcategory: 'F&I',
    unit: 'currency',
    dataType: 'currency'
  },
  'C0552.1': {
    code: 'C0552.1',
    displayName: 'Used Vehicle Gross Profit',
    description: 'Gross profit from used vehicle sales',
    category: KPICategory.FINANCE,
    subcategory: 'Used Vehicles',
    unit: 'currency',
    dataType: 'currency'
  },
  'C0550.1': {
    code: 'C0550.1',
    displayName: 'Total Direct Expenses',
    description: 'Total direct operational expenses',
    category: KPICategory.OPERATIONS,
    subcategory: 'Expenses',
    unit: 'currency',
    dataType: 'currency'
  },
  'C0200.1': {
    code: 'C0200.1',
    displayName: 'Personnel Expenses',
    description: 'Total personnel and payroll expenses',
    category: KPICategory.OPERATIONS,
    subcategory: 'Personnel',
    unit: 'currency',
    dataType: 'currency'
  },
  'C0600.1': {
    code: 'C0600.1',
    displayName: 'Total Direct Profit',
    description: 'Total direct profit before indirect expenses',
    category: KPICategory.PERFORMANCE,
    subcategory: 'Profitability',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0200.1': {
    code: 'A0200.1',
    displayName: 'Used Vehicle Units Sold',
    description: 'Total number of used vehicles sold in the period',
    category: KPICategory.SALES,
    subcategory: 'Used Vehicles',
    unit: 'units',
    dataType: 'units'
  },
  'A0200.2': {
    code: 'A0200.2',
    displayName: 'Used Vehicle Sales Revenue',
    description: 'Total revenue from used vehicle sales',
    category: KPICategory.SALES,
    subcategory: 'Used Vehicles',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0300.1': {
    code: 'A0300.1',
    displayName: 'Service Revenue',
    description: 'Total revenue from service department',
    category: KPICategory.SERVICE,
    subcategory: 'Service Operations',
    unit: 'currency',
    dataType: 'currency'
  },
  'A0300.2': {
    code: 'A0300.2',
    displayName: 'Service Labor Hours',
    description: 'Total billable labor hours in service department',
    category: KPICategory.SERVICE,
    subcategory: 'Service Operations',
    unit: 'hours',
    dataType: 'units'
  }
};

/**
 * Validate a KPI object
 */
export const validateKPI = (kpi: Partial<KPI>): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!kpi.code || typeof kpi.code !== 'string' || kpi.code.trim() === '') {
    errors.push('KPI code is required and must be a non-empty string');
  }

  if (!kpi.name || typeof kpi.name !== 'string' || kpi.name.trim() === '') {
    errors.push('KPI name is required and must be a non-empty string');
  }

  if (kpi.value === undefined || kpi.value === null || typeof kpi.value !== 'number' || isNaN(kpi.value)) {
    errors.push('KPI value must be a valid number');
  }

  // Removing category validation to allow any category
  // Categories are now free-form and not restricted to enum values

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Enrich a KPI with metadata based on its code
 */
export const enrichKPI = (code: string, value: number, customEnrichment?: KPIEnrichment): KPI => {
  const standardMapping = STANDARD_KPI_MAPPINGS[code];
  const enrichment = customEnrichment || standardMapping;

  if (enrichment) {
    return {
      code,
      name: enrichment.displayName,
      value,
      category: enrichment.category,
      subcategory: enrichment.subcategory,
      unit: enrichment.unit,
      description: enrichment.description
    };
  }

  // Default enrichment for unknown KPI codes
  return {
    code,
    name: `KPI ${code}`,
    value,
    category: 'Other', // Using string instead of enum
    subcategory: 'Unknown', // Always include subcategory with default value "Unknown"
    unit: 'unknown'
  };
};

/**
 * Bulk fetch historical KPI enrichments from database
 */
export const fetchHistoricalEnrichments = async (kpiCodes: string[]): Promise<Map<string, KPIEnrichment>> => {
  const enrichmentMap = new Map<string, KPIEnrichment>();
  
  if (kpiCodes.length === 0) {
        return enrichmentMap;
  }
  
  try {
    // Determine the API base URL based on environment
    const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
    
                    const requestUrl = `${apiBaseUrl}/kpi-enrichments/by-codes`;
    const requestBody = JSON.stringify({ codes: kpiCodes });
    
            const response = await fetch(requestUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: requestBody,
    });
    
            if (response.ok) {
      const result = await response.json();
            if (result.success && result.data) {
        const historicalEnrichments = result.data;
        
                        // Convert database format to KPIEnrichment format and store in map
        historicalEnrichments.forEach((enrichment: any) => {
          const kpiEnrichment: KPIEnrichment = {
            code: enrichment.kpiCode,
            displayName: enrichment.name,
            description: enrichment.description || `${enrichment.name}${enrichment.subcategory ? ` - ${enrichment.subcategory}` : ''}`,
            category: enrichment.category,
            subcategory: enrichment.subcategory,
            unit: enrichment.units,
            dataType: 'currency' // Default, could be enhanced
          };
          
                    enrichmentMap.set(enrichment.kpiCode, kpiEnrichment);
        });
        
                      } else {
        console.warn(`DEBUG KPI UTILS: Response not successful or no data:`, result);
      }
    } else {
      console.warn(`DEBUG KPI UTILS: Failed to fetch historical enrichments: ${response.status} ${response.statusText}`);
      const responseText = await response.text();
      console.warn(`DEBUG KPI UTILS: Response body:`, responseText);
    }
  } catch (error) {
    console.error('Error fetching historical enrichments:', error);
  }
  
  return enrichmentMap;
};

/**
 * Enrich a KPI with database lookup for learned metadata
 */
export const enrichKPIWithDatabase = async (code: string, value: number, customEnrichment?: KPIEnrichment): Promise<KPI> => {
  try {
    // Determine the API base URL based on environment
    const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
    
    // First, try to get historical enrichment from database
    const response = await fetch(`${apiBaseUrl}/kpi-enrichments/code/${encodeURIComponent(code)}`);
    
    if (response.ok) {
      const result = await response.json();
      if (result.success && result.data) {
        const historicalEnrichment = result.data;
        
                // Convert database format to KPIEnrichment format
        const dbEnrichment: KPIEnrichment = {
          code: historicalEnrichment.kpiCode,
          displayName: historicalEnrichment.name,
          description: historicalEnrichment.description || `${historicalEnrichment.name}${historicalEnrichment.subcategory ? ` - ${historicalEnrichment.subcategory}` : ''}`,
          category: historicalEnrichment.category,
          subcategory: historicalEnrichment.subcategory,
          unit: historicalEnrichment.units,
          dataType: 'currency' // Default, could be enhanced
        };
        
        // Use historical enrichment to create the KPI
        const enrichedKPI = enrichKPI(code, value, dbEnrichment);
                return enrichedKPI;
      }
    } else if (response.status === 404) {
          } else {
      console.warn(`DEBUG KPI UTILS: API error for KPI ${code}: ${response.status} ${response.statusText}`);
    }
    
    // If no historical data found, use custom enrichment or fall back to standard
    const fallbackKPI = enrichKPI(code, value, customEnrichment);
        return fallbackKPI;
    
  } catch (error) {
    console.warn(`Failed to fetch historical enrichment for KPI ${code}:`, error);
    // Fall back to standard enrichment on error
    return enrichKPI(code, value, customEnrichment);
  }
};

/**
 * Parse and validate numeric value from string
 */
export const parseKPIValue = (value: string | number | null | undefined): number => {
  if (typeof value === 'number') {
    return value;
  }

  if (value === null || value === undefined || typeof value !== 'string') {
    return 0;  // Return 0 for null, undefined, or non-string values
  }

  // Clean the value: remove currency symbols, commas, etc.
  let cleanValue = value.replace(/[$£€¥,]/g, '');

  // Handle percentage format
  if (cleanValue.includes('%')) {
    cleanValue = cleanValue.replace(/%/g, '');
    // Don't divide by 100 here - let the caller decide
  }

  // Handle parentheses for negative numbers
  if (cleanValue.startsWith('(') && cleanValue.endsWith(')')) {
    cleanValue = '-' + cleanValue.slice(1, -1);
  }

  // Final cleanup and conversion
  cleanValue = cleanValue.trim();
  const numValue = parseFloat(cleanValue);
  return isNaN(numValue) ? 0 : numValue;
};

/**
 * Group financial input data by brand, dealer, year, month
 */
export const groupFinancialInputs = (inputs: FinancialRecordInput[]): Map<string, FinancialRecordInput[]> => {
  const groups = new Map<string, FinancialRecordInput[]>();

  inputs.forEach(input => {
    const key = `${input.brand}|${input.dealer}|${input.year}|${input.month}`;
    
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    
    groups.get(key)!.push(input);
  });

  return groups;
};

/**
 * Convert grouped inputs to financial records
 */
export const convertInputsToRecords = (
  groupedInputs: Map<string, FinancialRecordInput[]>,
  kpiEnrichments?: Map<string, KPIEnrichment>
): FinancialRecord[] => {
  const records: FinancialRecord[] = [];

  groupedInputs.forEach((inputs, key) => {
    const [brand, dealer, year, month] = key.split('|');
    
    if (inputs.length === 0) return;
    
    // Use the first input's additionalData if available
    const additionalData = inputs[0].additionalData;
    
    // Group KPIs and detect duplicates
    const kpiMap = new Map<string, KPI>();
    
    inputs.forEach(input => {
      // Process each KPI in the input
      input.kpis.forEach(kpiInput => {
        // Create enrichment from input metadata if provided
        let enrichment: KPIEnrichment | undefined;
        
        if (kpiInput.name || kpiInput.category || kpiInput.subcategory || kpiInput.unit) {
          // Create enrichment from input metadata
          enrichment = {
            code: kpiInput.code,
            displayName: kpiInput.name || kpiInput.code,
            description: `${kpiInput.name || kpiInput.code}${kpiInput.subcategory ? ` - ${kpiInput.subcategory}` : ''}`,
            category: kpiInput.category || KPICategory.OTHER,
            subcategory: kpiInput.subcategory,
            unit: kpiInput.unit || 'unknown',
            dataType: 'currency' // Default, could be inferred from units
          };
        } else {
          // Use provided enrichments or standard mappings
          enrichment = kpiEnrichments?.get(kpiInput.code);
        }
        
        const kpi = enrichKPI(kpiInput.code, kpiInput.value, enrichment);
        
        // Handle duplicate KPI codes - take the last value or sum them
        if (kpiMap.has(kpiInput.code)) {
          console.warn(`Duplicate KPI code found: ${kpiInput.code} for ${brand}-${dealer}-${year}-${month}`);
          // For now, take the latest value. Could be configurable.
          kpiMap.set(kpiInput.code, kpi);
        } else {
          kpiMap.set(kpiInput.code, kpi);
        }
      });
    });

    const record: FinancialRecord = {
      id: `${brand}-${dealer}-${year}-${month}`, // Temporary ID, should be generated by backend
      brand,
      dealer,
      year: parseInt(year),
      month: parseInt(month),
      kpis: Array.from(kpiMap.values()),
      additionalData: additionalData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    records.push(record);
  });

  return records;
};

/**
 * Convert grouped inputs to financial records with database enrichment
 */
export const convertInputsToRecordsWithDatabase = async (
  groupedInputs: Map<string, FinancialRecordInput[]>,
  kpiEnrichments?: Map<string, KPIEnrichment>
): Promise<FinancialRecord[]> => {
  const records: FinancialRecord[] = [];

  // First, collect all unique KPI codes to bulk fetch historical enrichments
  const allKpiCodes = new Set<string>();
  groupedInputs.forEach(inputs => {
    inputs.forEach(input => {
      input.kpis.forEach(kpi => {
        allKpiCodes.add(kpi.code);
      });
    });
  });

    // Bulk fetch historical enrichments for better performance
  const historicalEnrichments = await fetchHistoricalEnrichments(Array.from(allKpiCodes));
  
    for (const entry of Array.from(groupedInputs.entries())) {
    const [key, inputs] = entry;
    const [brand, dealer, year, month] = key.split('|');
    
    if (inputs.length === 0) continue;
    
    // Use the first input's additionalData if available
    const additionalData = inputs[0].additionalData;
    
    // Group KPIs and detect duplicates
    const kpiMap = new Map<string, KPI>();
    
    for (const input of inputs) {
      // Process each KPI in the input
      for (const kpiInput of input.kpis) {
        // Check if CSV provided complete metadata for this KPI
        // Note: subcategory and units are optional and can be blank
        const hasCSVMetadata = kpiInput.name && kpiInput.name.trim() !== '' && 
                              kpiInput.category && kpiInput.category.trim() !== ''; 
                              // No longer requiring units to be present
        
        let enrichment: KPIEnrichment | undefined;
        
        if (hasCSVMetadata) {
          // Create enrichment directly from CSV column data
                    // Create enrichment from CSV metadata
          enrichment = {
            code: kpiInput.code,
            displayName: kpiInput.name || `KPI ${kpiInput.code}`,
            description: `${kpiInput.name}${kpiInput.subcategory ? ` - ${kpiInput.subcategory}` : ''}`,
            category: kpiInput.category || 'Other',
            subcategory: kpiInput.subcategory || 'Unknown', // Set subcategory to "Unknown" when missing
            unit: kpiInput.unit || 'unknown',
            dataType: kpiInput.unit?.toLowerCase().includes('$') ? 'currency' : 'units' // Infer data type from unit
          };
        } else {
          // If no CSV metadata, check historical enrichments, then provided enrichments
          enrichment = historicalEnrichments.get(kpiInput.code) || kpiEnrichments?.get(kpiInput.code);
          
                                      }
        
        // Use enrichment to create KPI (no need for individual database calls now)
        const kpi = enrichKPI(kpiInput.code, kpiInput.value, enrichment);
        
                // Handle duplicate KPI codes - take the last value or sum them
        if (kpiMap.has(kpiInput.code)) {
          console.warn(`Duplicate KPI code found: ${kpiInput.code} for ${brand}-${dealer}-${year}-${month}`);
          // For now, take the latest value. Could be configurable.
          kpiMap.set(kpiInput.code, kpi);
        } else {
          kpiMap.set(kpiInput.code, kpi);
        }
      }
    }

    const record: FinancialRecord = {
      id: `${brand}-${dealer}-${year}-${month}`, // Temporary ID, should be generated by backend
      brand,
      dealer,
      year: parseInt(year),
      month: parseInt(month),
      kpis: Array.from(kpiMap.values()),
      additionalData: additionalData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    records.push(record);
  }

    return records;
};

/**
 * Validate financial input data
 */
export const validateFinancialInputs = (inputs: FinancialRecordInput[]): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check if inputs array is empty
  if (inputs.length === 0) {
    errors.push('No input records provided');
    return { isValid: false, errors };
  }
  
  // Validate each input
  inputs.forEach((input, index) => {
    // Check required fields
    if (!input.dealer) {
      errors.push(`Input ${index + 1}: Missing dealer`);
    }
    
    if (!input.brand) {
      errors.push(`Input ${index + 1}: Missing brand`);
    }
    
    if (input.year === undefined || isNaN(input.year)) {
      errors.push(`Input ${index + 1}: Invalid or missing year`);
    }
    
    if (input.month === undefined || isNaN(input.month) || input.month < 1 || input.month > 12) {
      errors.push(`Input ${index + 1}: Invalid or missing month (must be 1-12)`);
    }
    
    // Check KPIs
    if (!input.kpis || !Array.isArray(input.kpis) || input.kpis.length === 0) {
      errors.push(`Input ${index + 1}: Missing KPIs array`);
    } else {
      // Validate each KPI
      input.kpis.forEach((kpi, kpiIndex) => {
        if (!kpi.code) {
          errors.push(`Input ${index + 1}, KPI ${kpiIndex + 1}: Missing KPI code`);
        }
        
        if (kpi.value === undefined || isNaN(kpi.value)) {
          errors.push(`Input ${index + 1}, KPI ${kpiIndex + 1}: Invalid or missing value for KPI ${kpi.code || 'unknown'}`);
        }
      });
    }
  });
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Get KPI statistics from a collection of financial records
 */
export const getKPIStatistics = (records: FinancialRecord[]) => {
  const stats = {
    totalKPIs: 0,
    uniqueKPICodes: new Set<string>(),
    categoryCounts: new Map<string, number>(),
    avgKPIsPerRecord: 0,
    duplicateKPIs: [] as string[]
  };

  records.forEach(record => {
    const recordKPICodes = new Set<string>();
    
    record.kpis.forEach(kpi => {
      stats.totalKPIs++;
      stats.uniqueKPICodes.add(kpi.code);
      
      // Track duplicates within the same record
      if (recordKPICodes.has(kpi.code)) {
        stats.duplicateKPIs.push(`${record.brand}-${record.dealer}-${record.year}-${record.month}: ${kpi.code}`);
      }
      recordKPICodes.add(kpi.code);

      // Count by category
      const category = kpi.category || 'Unknown';
      stats.categoryCounts.set(category, (stats.categoryCounts.get(category) || 0) + 1);
    });
  });

  stats.avgKPIsPerRecord = records.length > 0 ? stats.totalKPIs / records.length : 0;

  return {
    totalKPIs: stats.totalKPIs,
    uniqueKPICodes: stats.uniqueKPICodes.size,
    uniqueKPIList: Array.from(stats.uniqueKPICodes),
    categoryBreakdown: Array.from(stats.categoryCounts.entries()).map(([category, count]) => ({
      category,
      count
    })),
    avgKPIsPerRecord: Math.round(stats.avgKPIsPerRecord * 100) / 100,
    duplicateKPIs: stats.duplicateKPIs,
    hasDuplicates: stats.duplicateKPIs.length > 0
  };
};

/**
 * Convert new KPI-based FinancialRecord to legacy flat structure for backward compatibility
 */
export const convertToLegacyFormat = (record: FinancialRecord): any => {
  const legacy: any = {
    id: record.id,
    dealerCode: record.dealer.replace(/\s+/g, '_').toUpperCase(),
    dealerName: record.dealer,
    year: record.year,
    month: record.month,
    brand: record.brand,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt
  };

  // Convert KPIs to flat properties using standard mappings
  record.kpis.forEach(kpi => {
    const mapping = STANDARD_KPI_MAPPINGS[kpi.code];
    if (mapping) {
      // Use a standardized property name based on the KPI code
      const propName = getPropertyNameFromKPI(kpi.code);
      legacy[propName] = kpi.value;
    } else {
      // For unmapped KPIs, create a safe property name
      const safeName = kpi.code.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase();
      legacy[`kpi_${safeName}`] = kpi.value;
    }
  });

  return legacy;
};

/**
 * Get a property name for a KPI code for backward compatibility
 */
const getPropertyNameFromKPI = (kpiCode: string): string => {
  const kpiToPropertyMap: Record<string, string> = {
    'A0100.1': 'newVehicleUnitsSold',
    'A0100.2': 'newVehicleSalesRevenue',
    'A0100.4': 'newVehicleGrossProfit',
    'A0200.1': 'usedVehicleUnitsSold',
    'A0200.2': 'usedVehicleSalesRevenue',
    'A0300.1': 'serviceRevenue',
    'A0300.2': 'serviceLabourHours',
    'A0480.1': 'financeInsuranceIncome',
    'C0552.1': 'usedVehicleGrossProfit',
    'C0550.1': 'totalDirectExpenses',
    'C0200.1': 'personnelExpenses',
    'C0600.1': 'totalDirectProfit',
    // Add more mappings as needed
  };

  return kpiToPropertyMap[kpiCode] || `kpi_${kpiCode.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase()}`;
};

/**
 * Convert an array of KPI-based records to legacy format for data grids
 */
export const convertArrayToLegacyFormat = (records: FinancialRecord[]): any[] => {
  return records.map(convertToLegacyFormat);
};

/**
 * Create a mock legacy record structure for testing
 */
export const createMockLegacyRecord = (
  dealer: string,
  brand: string,
  year: number,
  month: number
): any => {
  return {
    id: `mock_${dealer}_${year}_${month}`,
    dealerCode: dealer.replace(/\s+/g, '_').toUpperCase(),
    dealerName: dealer,
    year,
    month,
    brand,
    newVehicleUnitsSold: Math.floor(Math.random() * 100) + 50,
    newVehicleSalesRevenue: Math.floor(Math.random() * 1000000) + 500000,
    newVehicleGrossProfit: Math.floor(Math.random() * 100000) + 50000,
    usedVehicleUnitsSold: Math.floor(Math.random() * 50) + 25,
    usedVehicleSalesRevenue: Math.floor(Math.random() * 500000) + 250000,
    usedVehicleGrossProfit: Math.floor(Math.random() * 50000) + 25000,
    financeInsuranceIncome: Math.floor(Math.random() * 25000) + 10000,
    partsServiceRevenue: Math.floor(Math.random() * 75000) + 30000,
    partsServiceGrossProfit: Math.floor(Math.random() * 30000) + 15000,
    totalDirectExpenses: Math.floor(Math.random() * 50000) + 25000,
    personnelExpenses: Math.floor(Math.random() * 30000) + 15000,
    advertisingExpenses: Math.floor(Math.random() * 10000) + 5000,
    totalDirectProfit: Math.floor(Math.random() * 75000) + 35000,
    newVehiclesDiscountAmount: Math.floor(Math.random() * 15000) + 5000,
    newVehiclesFinancePenetration: Math.floor(Math.random() * 100) + 50,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  };
}; 